import { useCallback, useEffect, useRef, useState } from "react";
import { useApplicationLinksHook } from "../../hooks/useApplicationLinksHook";
import { sanitizePrefill } from "../../constants/borrowerLinkPrefill";

/**
 * Resolves the link in the URL into prefill for a public form.
 *
 * Contract: lendmarq-os `frontend_documentation/application-links-and-external-
 * submissions.md` section 3.
 *
 * Two link generations, both of which must keep working:
 *   - new:    ?t=<token>
 *   - legacy: ?link_id=<oid>&link_type=app|auth   (985 live in the wild)
 *
 * Returns:
 *   linkStatus — null (no link in the URL: a plain visit, render the form),
 *                "ok", "revoked", "expired", "consumed", or "not_found".
 *                Anything other than null/"ok" means the form must NOT be
 *                offered — prefill is null for those and the borrower needs a
 *                fresh link from their loan officer.
 *   prefill    — sanitized values to hydrate the form.
 *   checked    — hold the form back until true so react-final-form mounts once
 *                with its real initialValues.
 *   token/linkId — passed to the submit call, which requires them.
 *   reportStarted — call on first field interaction.
 *   hasUrlParams — the URL carries context beyond pure presentation, so this
 *                visit was arrived at from somewhere rather than typed in.
 */

/**
 * Params that only change how the page looks. They carry no borrower data, so
 * an unbranded embed (`?hideLogo=true`) still counts as a plain visit.
 */
const PRESENTATION_PARAMS = ["hideLogo", "brandingName"];

/**
 * Whether the URL carries anything beyond presentation — a link token, a legacy
 * `link_id`, or any other param a future caller appends.
 *
 * Read once, synchronously, so the first render already knows. The link resolve
 * is async, and gating on the resolved `prefill` instead would flash the draft
 * banner for the round-trip and then yank it away.
 */
const readHasUrlParams = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of params.keys()) {
      if (!PRESENTATION_PARAMS.includes(key)) return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

// Stable for the visit, per the beacon's dedupe key. sessionStorage rather than
// state: a step change or remount must not read as a second visit.
const SESSION_KEY_STORAGE = "brokerPortal.publicLink.sessionKey";

const getSessionKey = () => {
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY_STORAGE);
    if (existing) return existing;
    const fresh = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.sessionStorage.setItem(SESSION_KEY_STORAGE, fresh);
    return fresh;
  } catch (e) {
    // Private mode / blocked storage: a per-render key still beacons, it just
    // dedupes less well. Better than throwing on the borrower's page load.
    return `${Date.now()}-nostore`;
  }
};

export const usePublicLinkPrefill = (linkKind) => {
  const { resolveLink, trackLinkEvent } = useApplicationLinksHook();
  const [prefill, setPrefill] = useState(null);
  const [linkStatus, setLinkStatus] = useState(null);
  const [recipientName, setRecipientName] = useState(null);
  const [checked, setChecked] = useState(false);
  // Constant for the visit — the URL is read once, same as the effect below.
  const [hasUrlParams] = useState(readHasUrlParams);

  const linkRef = useRef({ token: null, linkId: null });
  const startedRef = useRef(false);

  useEffect(() => {
    let params = null;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      setChecked(true);
      return;
    }

    const token = params.get("t");
    const linkId = params.get("link_id");
    const linkType = params.get("link_type");

    if (!token && !linkId) {
      // No link at all — a direct visit. Blank form, no tracking.
      setChecked(true);
      return;
    }

    linkRef.current = { token, linkId };
    const sessionKey = getSessionKey();

    resolveLink({
      token,
      linkId,
      linkType,
      onSuccessFn: (data) => {
        const status = data?.status || "not_found";
        setLinkStatus(status);
        setRecipientName(data?.recipient_name || null);
        if (status === "ok") {
          // Sanitized server-side too; re-running it costs nothing and keeps one
          // rule for what a form will accept.
          const values = sanitizePrefill(data?.prefill, linkKind);
          if (Object.keys(values).length > 0) setPrefill(values);
          trackLinkEvent({ event: "opened", token, linkId, sessionKey });
        }
      },
      // 404 is "unknown link" — a page state, not an error to swallow silently.
      onFailFn: () => setLinkStatus("not_found"),
      onCompleteFn: () => setChecked(true),
    });
    // resolveLink/trackLinkEvent are recreated each render; the URL is read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkKind]);

  const reportStarted = useCallback(() => {
    const { token, linkId } = linkRef.current;
    if (startedRef.current || (!token && !linkId)) return;
    startedRef.current = true;
    trackLinkEvent({
      event: "started",
      token,
      linkId,
      sessionKey: getSessionKey(),
    });
    // Guarded by the ref, so the identity of trackLinkEvent cannot cause a resend.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    prefill,
    checked,
    linkStatus,
    recipientName,
    token: linkRef.current.token,
    linkId: linkRef.current.linkId,
    reportStarted,
    hasUrlParams,
  };
};

export default usePublicLinkPrefill;
